import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Container, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { submitBackgroundSurvey } from "./slice";
import { setSurveyComplete } from "../../containers/Dashboard/slice";
import "./backgroundSurvey.css";

const BackgroundSurvey = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isBackgroundSurveyLoading, backgroundSurveyError } = useSelector(
    (state) => state.createAccount
  );

  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState({
    role: "",
    experience: "",
    companySize: "",
    inspectionNumber: "",
    inspectionTypes: [],
    currentTools: "",
    expectation: "",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        inspectionTypes: checked
          ? [...prev.inspectionTypes, value]
          : prev.inspectionTypes.filter((type) => type !== value),
      }));
    } else if (type === "textarea") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const isFormValid = () => {
    return (
      formData.role &&
      formData.experience &&
      formData.companySize &&
      formData.inspectionNumber &&
      formData.inspectionTypes.length > 0
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    const payload = {
      role: formData.role,
      inspection_experience: formData.experience,
      company_size: formData.companySize,
      annual_inspection_number: formData.inspectionNumber,
      inspection_type_offered: formData.inspectionTypes.join(", "),
      current_inspection_tools: formData.currentTools,
      expectation_from_an_AI_tool: formData.expectation,
    };

    const resultAction = await dispatch(submitBackgroundSurvey(payload));

    if (submitBackgroundSurvey.fulfilled.match(resultAction)) {
      dispatch(setSurveyComplete(true));
      navigate("/");
      window.scrollTo(0, 0);
    }
  };

  return (
    <Container className="background-survey-container">
      <h1 className="text-center mb-4 page-title">Share Your Background</h1>
      <Form
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
        className="background-survey-form"
      >
        <Form.Group>
          <Form.Label>Role*</Form.Label>
          <div className="options-grid options-2-col">
            <Form.Check
              required
              type="radio"
              name="role"
              value="Inspector"
              label="Inspector"
              id="role-inspector"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="role"
              value="Company Owner"
              label="Company Owner"
              id="role-owner"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="role"
              value="Apprentice"
              label="Apprentice"
              id="role-apprentice"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="role"
              value="Other"
              label="Other"
              id="role-other"
              onChange={handleInputChange}
            />
          </div>
        </Form.Group>

        <hr className="divider" />

        <Form.Group>
          <Form.Label>Inspection Experience*</Form.Label>
          <div className="options-grid options-3-col">
            <Form.Check
              required
              type="radio"
              name="experience"
              value="Less than 1 year"
              label="Less than 1 year"
              id="exp-1"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="experience"
              value="1-2 years"
              label="1-2 years"
              id="exp-2"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="experience"
              value="3-5 years"
              label="3-5 years"
              id="exp-3"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="experience"
              value="6-10 years"
              label="6-10 years"
              id="exp-4"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="experience"
              value="11 years+"
              label="11 years+"
              id="exp-5"
              onChange={handleInputChange}
            />
          </div>
        </Form.Group>

        <hr className="divider" />

        <Form.Group>
          <Form.Label>Company Size*</Form.Label>
          <div className="options-grid options-3-col">
            <Form.Check
              required
              type="radio"
              name="companySize"
              value="Just me"
              label="Just me"
              id="size-1"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="companySize"
              value="2-4 people"
              label="2-4 people"
              id="size-2"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="companySize"
              value="5-10 people"
              label="5-10 people"
              id="size-3"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="companySize"
              value="11-25 people"
              label="11-25 people"
              id="size-4"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="companySize"
              value="26 people+"
              label="26 people+"
              id="size-5"
              onChange={handleInputChange}
            />
          </div>
        </Form.Group>

        <hr className="divider" />

        <Form.Group>
          <Form.Label>Annual Inspection Number*</Form.Label>
          <div className="options-grid options-3-col">
            <Form.Check
              required
              type="radio"
              name="inspectionNumber"
              value="0-50"
              label="0-50"
              id="insp-1"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="inspectionNumber"
              value="51-100"
              label="51-100"
              id="insp-2"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="inspectionNumber"
              value="101-200"
              label="101-200"
              id="insp-3"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="inspectionNumber"
              value="201-300"
              label="201-300"
              id="insp-4"
              onChange={handleInputChange}
            />
            <Form.Check
              type="radio"
              name="inspectionNumber"
              value="300+"
              label="300+"
              id="insp-5"
              onChange={handleInputChange}
            />
          </div>
        </Form.Group>

        <hr className="divider" />

        <Form.Group>
          <Form.Label>Inspection Type Offered*</Form.Label>
          <div className="options-grid options-2-col">
            <Form.Check
              type="checkbox"
              name="inspectionTypes"
              value="Residential"
              label="Residential"
              id="type-residential"
              onChange={handleInputChange}
            />
            <Form.Check
              type="checkbox"
              name="inspectionTypes"
              value="Commercial"
              label="Commercial"
              id="type-commercial"
              onChange={handleInputChange}
            />
            <Form.Check
              type="checkbox"
              name="inspectionTypes"
              value="New Construction"
              label="New Construction"
              id="type-construction"
              onChange={handleInputChange}
            />
            <Form.Check
              type="checkbox"
              name="inspectionTypes"
              value="Pre-Listing"
              label="Pre-Listing"
              id="type-listing"
              onChange={handleInputChange}
            />
            <Form.Check
              type="checkbox"
              name="inspectionTypes"
              value="Specialty (e.g., Mold, Radon)"
              label="Specialty (e.g., Mold, Radon)"
              id="type-specialty"
              onChange={handleInputChange}
            />
          </div>
        </Form.Group>

        <hr className="divider" />

        <Form.Group>
          <Form.Label>Current Inspection Tools</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="currentTools"
            value={formData.currentTools}
            onChange={handleInputChange}
            placeholder="Please list the inspection tools you currently use..."
          />
        </Form.Group>

        <hr className="divider" />

        <Form.Group>
          <Form.Label>Expectation from an AI inspection tool</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="expectation"
            value={formData.expectation}
            onChange={handleInputChange}
            placeholder="What are your expectations from an AI inspection tool..."
          />
        </Form.Group>

        {backgroundSurveyError && (
          <div className="text-danger mb-3">{backgroundSurveyError}</div>
        )}

        <div className="d-flex justify-content-center mt-4">
          <Button
            type="submit"
            className="primary-action-button continue-button"
            disabled={!isFormValid() || isBackgroundSurveyLoading}
          >
            {isBackgroundSurveyLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="loading-button-spinner"
              />
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default BackgroundSurvey;
